const DEV_CORE_API_URL = "http://localhost:8081";
const DEV_AUTH_API_URL = "http://localhost:8086";

function normalizeBaseUrl(url) {
	return String(url || "").trim().replace(/\/$/, "");
}

function getApiBaseUrl() {
	const configured = normalizeBaseUrl(process.env.REACT_APP_API_URL || "");

	if (!configured) {
		return DEV_CORE_API_URL;
	}

	if (/^:\d+$/.test(configured)) {
		return `http://localhost${configured}`;
	}

	if (/^localhost:\d+$/i.test(configured)) {
		return `http://${configured}`;
	}

	return configured;
}

const API_BASE_URL = getApiBaseUrl();

function getAuthBaseUrl() {
	const configured = normalizeBaseUrl(process.env.REACT_APP_AUTH_URL || "https://striver.aftrprty.shop");

	if (!configured) {
		return DEV_AUTH_API_URL;
	}

	if (/^:\d+$/.test(configured)) {
		return `http://localhost${configured}`;
	}

	if (/^localhost:\d+$/i.test(configured)) {
		return `http://${configured}`;
	}

	return configured;
}

const AUTH_BASE_URL = getAuthBaseUrl();

async function fetchJson(url, options = {}) {
	const response = await fetch(url, options);

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	return { response, payload };
}

export async function submitChallengeAnswer(answer) {
	const response = await fetch(`${API_BASE_URL}/api/auth/challenge`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ answer }),
	});

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	if (!response.ok) {
		throw new Error(payload.error || "Could not verify answer");
	}

	return payload;
}

export async function selectProfile(userId) {
	const response = await fetch(`${API_BASE_URL}/api/auth/select-profile`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ userId }),
	});

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	if (!response.ok) {
		throw new Error(payload.error || "Could not select profile");
	}

	return payload;
}

export async function getSession() {
	let coreError = null;

	try {
		const { response, payload } = await fetchJson(`${AUTH_BASE_URL}/auth/session`, {
			method: "GET",
			credentials: "include",
		});

		if (response.ok) {
			return payload;
		}
	} catch {
		// Fall through to core-compatible session endpoint.
	}

	const coreResult = await fetchJson(`${API_BASE_URL}/api/auth/session`, {
		method: "GET",
		credentials: "include",
	});

	if (!coreResult.response.ok) {
		coreError = coreResult.payload.error || "Not authenticated";
		throw new Error(coreError);
	}

	return coreResult.payload;
}

export async function logout() {
	const attempts = await Promise.allSettled([
		fetchJson(`${AUTH_BASE_URL}/auth/logout`, {
			method: "POST",
			credentials: "include",
		}),
		fetchJson(`${API_BASE_URL}/api/auth/logout`, {
			method: "POST",
			credentials: "include",
		}),
	]);

	const successfulAttempt = attempts.find(
		(result) => result.status === "fulfilled" && result.value.response.ok
	);

	if (successfulAttempt && successfulAttempt.status === "fulfilled") {
		return successfulAttempt.value.payload;
	}

	const firstFailure = attempts.find((result) => result.status === "fulfilled");
	if (firstFailure && firstFailure.status === "fulfilled") {
		throw new Error(firstFailure.value.payload.error || "Failed to logout");
	}

	throw new Error("Failed to logout");
}

export async function getSecurityQuestion() {
  const response = await fetch(`${API_BASE_URL}/api/auth/security-question`, {
    method: "GET",
    credentials: "include",
  });

  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    throw new Error(payload.error || "Could not load security question");
  }

  return payload;
}

export async function setSecurityQuestion(userId, question, answer) {
  const response = await fetch(`${API_BASE_URL}/api/auth/security-question/set`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ userId, question, answer }),
  });
  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }
  if (!response.ok) {
    throw new Error(payload.error || "Could not save security question");
  }
  return payload;
}

export async function verifySecurityQuestion(userId, answer) {
  const response = await fetch(`${API_BASE_URL}/api/auth/security-question/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ userId, answer }),
  });
  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }
  if (!response.ok) {
    throw new Error(payload.error || "Incorrect answer");
  }
  return payload;
}